/* ============================================================================
   UI / feedback — state and messages.
   Badge, Alert, Callout, Quote, EmptyState, Spinner, Progress.
   ============================================================================ */
import React from "react";
import { cx } from "./util.js";

/* A small label for a state or count; `variant` is success, warning, danger, info or neutral, `dot` adds a coloured dot. */
export function Badge({ variant, dot, className, children, ...props }) {
  return (
    <span className={cx("ds-badge", variant && `ds-badge--${variant}`, className)} {...props}>
      {dot && <span className="ds-badge__dot" />}{children}
    </span>
  );
}

/* One line of feedback; tone is success, warning, danger or (default) neutral. */
export function Alert({ tone, children, className, ...props }) {
  return <div role={tone === "danger" ? "alert" : undefined} className={cx("ds-alert", tone && `ds-alert--${tone}`, className)} {...props}>{children}</div>;
}

/* The plain line shown where a list has nothing in it yet; say what is missing, in the customer's words. */
export function EmptyState({ children }) {
  return <div className="ds-empty">{children}</div>;
}

/* A small turning ring for "this is being saved"; the label is for screen readers. */
export function Spinner({ label = "Working" }) {
  return <span className="ds-spinner" role="status" aria-label={label} />;
}

/* A framed note inside a card or a page: tone is danger, info, success or warning (the frame and the title take
   it); `soft` is a quiet filled panel with no frame; `dashed` marks something still open. `icon` goes before the title. */
export function Callout({ tone, soft, dashed, icon, title, className, children }) {
  return (
    <div className={cx("ds-callout", tone && `ds-callout--${tone}`, soft && "ds-callout--soft", dashed && "ds-callout--dashed", className)}>
      {title && <div className="ds-callout__title">{icon}{title}</div>}
      {children}
    </div>
  );
}

/* A line quoted from someone, set apart by a bar on the left; tone is info (default) or success. */
export function Quote({ tone, children }) {
  return <blockquote className={cx("ds-quote", tone && `ds-quote--${tone}`)}>{children}</blockquote>;
}

/* A thin bar for how far something has got: `value` is 0 to 100 (kept inside that); size "lg" is the thick one. It is
   decoration unless `label` names what is measured, which makes it a progress bar for screen readers. */
export function Progress({ value, size, label, className }) {
  const percent = Math.min(100, Math.max(0, Number(value) || 0));
  const aria = label ? { role: "progressbar", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": percent } : { "aria-hidden": "true" };
  return (
    <div className={cx("ds-progress", size && `ds-progress--${size}`, className)} {...aria}>
      <div style={{ width: `${percent}%` }} />
    </div>
  );
}
